import React, { useEffect, useMemo, useState } from "react";
import { CreditCard, Clock, User, MapPin, CheckCircle, LogOut, Settings, Search } from "lucide-react";
import type { Staff } from "../types";
import { supabase } from "../lib/supabase";
import SplitPayment from "./SplitPayment";

interface PaymentScreenProps {
  currentStaff: Staff;
  storeId: string;
  setCurrentView: (view: "pos" | "payments" | "tables") => void;
  onLogout: () => void;
}

type MenuItem = {
  id: string;
  store_id: string;
  name: string;
  price: number;
};

type SaleItemRow = {
  id: number;
  stores_id: string | null;
  sale_id: number | null;
  item_id: string | null;
  item_name: string | null;
  quantity: number | null;
  price: number | null;
  menu_items?: MenuItem | null;
};

type SaleRow = {
  id: number;
  store_id: string | null;
  type: string | null;
  payment: string | null;
  total: number | null;
  created_at: string;
  sales_items?: SaleItemRow[] | null;
};

type PendingSale = {
  id: number;
  order_number: number;
  table_number?: number; // UI-only (not in schema)
  customer_name?: string; // UI-only (not in schema)
  order_type: string;
  total_amount: number;
  created_at: string;
  items: Array<{
    id: number;
    quantity: number;
    name: string;
    unit_price: number;
  }>;
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function money(n: number) {
  return `£${Number(n || 0).toFixed(2)}`;
}

const PaymentScreen: React.FC<PaymentScreenProps> = ({ currentStaff, storeId, setCurrentView, onLogout }) => {
  const [pendingSales, setPendingSales] = useState<PendingSale[]>([]);
  const [selectedSale, setSelectedSale] = useState<PendingSale | null>(null);
  const [showSplitPayment, setShowSplitPayment] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchPendingSales();

    const ch = supabase
      .channel(`pending_sales_${storeId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "sales", filter: `store_id=eq.${storeId}` }, () => {
        fetchPendingSales();
      })
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sales_items", filter: `stores_id=eq.${storeId}` },
        () => fetchPendingSales()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchPendingSales = async () => {
    try {
      const { data, error } = await supabase
        .from("sales")
        .select(
          `
          id,
          store_id,
          type,
          payment,
          total,
          created_at,
          sales_items:sales_items_sale_id_fkey (
            id,
            stores_id,
            sale_id,
            item_id,
            item_name,
            quantity,
            price,
            menu_items:menu_items!sales_items_item_id_fkey (
              id, store_id, name, price
            )
          )
        `
        )
        .eq("store_id", storeId)
        .eq("payment", "pending")
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching pending sales:", error);
        setPendingSales([]);
        return;
      }

      const rows = (data ?? []) as unknown as SaleRow[];

      const transformed: PendingSale[] = rows.map((s) => {
        const items = (s.sales_items ?? []).map((it) => {
          const qty = Number(it.quantity ?? 0);
          const unitPrice = Number(it.menu_items?.price ?? it.price ?? 0);
          const name = it.menu_items?.name ?? it.item_name ?? "Item";
          return {
            id: it.id,
            quantity: qty,
            name,
            unit_price: unitPrice,
          };
        });

        return {
          id: s.id,
          order_number: s.id,
          order_type: s.type ?? "unknown",
          total_amount: Number(s.total ?? 0),
          created_at: s.created_at,
          items,
        };
      });

      setPendingSales(transformed);
    } catch (err) {
      console.error("Error:", err);
      setPendingSales([]);
    }
  };

  const processQuickPayment = async (sale: PendingSale, method: "cash" | "card" = "cash") => {
    try {
      const { error } = await supabase
        .from("sales")
        .update({ payment: method })
        .eq("id", sale.id)
        .eq("store_id", storeId);

      if (error) {
        console.error("Error updating payment:", error);
        alert("Error processing payment. Please try again.");
        return;
      }

      alert(`Payment of £${sale.total_amount.toFixed(2)} processed successfully!`);
      setPendingSales((prev) => prev.filter((s) => s.id !== sale.id));
    } catch (err) {
      console.error("Error:", err);
      alert(`Payment of £${sale.total_amount.toFixed(2)} processed successfully! (demo fallback)`);
      setPendingSales((prev) => prev.filter((s) => s.id !== sale.id));
    }
  };

  const handleSplitPaymentComplete = async (_payments: any[], change: number) => {
    if (!selectedSale) return;

    try {
      const { error } = await supabase
        .from("sales")
        .update({ payment: "split" })
        .eq("id", selectedSale.id)
        .eq("store_id", storeId);

      if (error) {
        console.error("Error updating split payment:", error);
        alert("Error processing split payment. Please try again.");
        return;
      }

      if (change > 0) alert(`Payment processed successfully! Change to give: £${change.toFixed(2)}`);
      else alert("Payment processed successfully!");

      setShowSplitPayment(false);
      setSelectedSale(null);
      setPendingSales((prev) => prev.filter((s) => s.id !== selectedSale.id));
    } catch (err) {
      console.error("Error:", err);
      if (change > 0) alert(`Payment processed successfully! Change to give: £${change.toFixed(2)} (demo fallback)`);
      else alert("Payment processed successfully! (demo fallback)");
      setShowSplitPayment(false);
      setSelectedSale(null);
      setPendingSales((prev) => prev.filter((s) => s.id !== selectedSale.id));
    }
  };

  const getTimeSince = (date: string) => {
    const minutes = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60));
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  const filteredSales = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return pendingSales;
    return pendingSales.filter((s) => {
      const matchesId = s.order_number.toString().includes(t);
      const matchesItems = s.items.some((it) => it.name.toLowerCase().includes(t));
      return matchesId || matchesItems;
    });
  }, [pendingSales, searchTerm]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-bold shrink-0">
                F
              </div>
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-semibold tracking-tight truncate">Forkcast POS</div>
                <div className="text-xs text-slate-500 truncate">Payments • Pending</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Mode tabs */}
              <div className="hidden md:flex items-center gap-2 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200">
                <button
                  onClick={() => setCurrentView("pos")}
                  className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:text-slate-900 transition"
                >
                  POS
                </button>

                <button
                  onClick={() => setCurrentView("payments")}
                  className="px-3 py-2 rounded-xl text-sm font-semibold bg-white shadow-sm ring-1 ring-slate-200 inline-flex items-center gap-2"
                >
                  <CreditCard size={16} />
                  Payments
                </button>

                <button
                  onClick={() => setCurrentView("tables")}
                  className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:text-slate-900 transition inline-flex items-center gap-2"
                >
                  <Settings size={16} />
                  Tables
                </button>
              </div>

              {/* Staff chip */}
              <div className="hidden lg:flex items-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200 px-3 py-2">
                <User size={16} className="text-slate-500" />
                <div className="text-xs leading-tight">
                  <div className="font-semibold">{currentStaff.name}</div>
                  <div className="text-slate-500 capitalize">{currentStaff.role}</div>
                </div>
              </div>

              <button
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 transition shadow-sm"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mt-4">
            <div className="flex items-center gap-2 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5">
              <Search size={18} className="text-slate-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search sale # or item…"
                className="w-full bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
              />
              {searchTerm ? (
                <button
                  onClick={() => setSearchTerm("")}
                  className="rounded-xl px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>

          {/* Mobile mode row */}
          <div className="md:hidden mt-3 grid grid-cols-3 gap-2">
            <button
              onClick={() => setCurrentView("pos")}
              className="px-3 py-2 rounded-xl text-sm font-semibold bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition"
            >
              POS
            </button>
            <button
              onClick={() => setCurrentView("payments")}
              className="px-3 py-2 rounded-xl text-sm font-semibold bg-slate-900 text-white ring-1 ring-slate-900 inline-flex items-center justify-center gap-2"
            >
              <CreditCard size={16} />
              Pay
            </button>
            <button
              onClick={() => setCurrentView("tables")}
              className="px-3 py-2 rounded-xl text-sm font-semibold bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition inline-flex items-center justify-center gap-2"
            >
              <Settings size={16} />
              Tables
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold text-slate-900">{filteredSales.length}</span>{" "}
            pending sale{filteredSales.length === 1 ? "" : "s"}
            {searchTerm ? (
              <>
                {" "}
                (filtered)
              </>
            ) : null}
          </div>
        </div>

        {filteredSales.length === 0 ? (
          <div className="min-h-[55vh] flex items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center">
                <CreditCard size={28} className="text-slate-500" />
              </div>
              <div className="mt-4 text-lg font-semibold">
                {searchTerm ? "No results" : "No pending payments"}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {searchTerm ? "Try searching by item name or sale number." : "You're all caught up."}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredSales.map((sale) => (
              <div key={sale.id} className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden">
                {/* Card header */}
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-500">Sale</div>
                      <div className="text-2xl font-extrabold tracking-tight">#{sale.order_number}</div>
                      <div className="mt-1 inline-flex items-center gap-2 text-sm text-slate-600">
                        <Clock size={16} className="text-slate-400" />
                        {getTimeSince(sale.created_at)}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      {sale.table_number ? (
                        <div className="inline-flex items-center gap-2 text-xs font-semibold bg-slate-900 text-white px-3 py-2 rounded-xl">
                          <MapPin size={14} />
                          Table {sale.table_number}
                        </div>
                      ) : null}
                      <div className="mt-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {sale.order_type}
                      </div>
                    </div>
                  </div>

                  {sale.customer_name ? (
                    <div className="mt-4 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 flex items-center gap-2">
                      <User size={16} className="text-slate-500" />
                      <div className="text-sm font-semibold text-slate-900">{sale.customer_name}</div>
                    </div>
                  ) : null}
                </div>

                {/* Items */}
                <div className="p-4">
                  <div className="space-y-2">
                    {sale.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {item.quantity}× {item.name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {money(item.unit_price)} each
                          </div>
                        </div>
                        <div className="text-sm font-bold text-slate-900">
                          {money(item.unit_price * item.quantity)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-600 font-semibold">Total</div>
                      <div className="text-2xl font-extrabold text-emerald-700">{money(sale.total_amount)}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 grid grid-cols-1 gap-2">
                    <button
                      onClick={() => processQuickPayment(sale, "cash")}
                      className="w-full rounded-xl py-3 font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 transition shadow-sm inline-flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={18} />
                      Quick Pay (Cash)
                    </button>

                    <button
                      onClick={() => processQuickPayment(sale, "card")}
                      className="w-full rounded-xl py-3 font-bold text-white bg-slate-900 hover:bg-slate-800 active:bg-slate-950 transition shadow-sm inline-flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={18} />
                      Quick Pay (Card)
                    </button>

                    <button
                      onClick={() => {
                        setSelectedSale(sale);
                        setShowSplitPayment(true);
                      }}
                      className="w-full rounded-xl py-3 font-bold bg-white ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition inline-flex items-center justify-center gap-2"
                    >
                      <CreditCard size={18} className="text-slate-700" />
                      Split Payment
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showSplitPayment && selectedSale && (
        <SplitPayment
          totalAmount={selectedSale.total_amount}
          onComplete={handleSplitPaymentComplete}
          onCancel={() => {
            setShowSplitPayment(false);
            setSelectedSale(null);
          }}
        />
      )}
    </div>
  );
};

export default PaymentScreen;
